import { GridLegacy, Stack } from '@mui/material';
import { Icon as TablerIcon } from '@tabler/icons-react';
import _ from 'lodash';

import { COLOR_OPTIONS } from 'types';
import { actionButtonSpacing } from 'constants/themeConstants';
import { useDeviceContext } from 'hooks/useDeviceContext';
import { RowAction } from 'types/datagrid';
import { FullOrIconButton } from 'ui-component/DataGrid/components/FullOrIconButton';
import { MoreActionsButton } from 'ui-component/DataGrid/components/MoreActionsButton';

type ActionBarProps<T> = {
  rowActions?: RowAction<T>[];
  selectedRowIds: string[];
  selectedRowData: T[];
};

export default function ActionBar<T>({
  rowActions = [],
  selectedRowIds,
  selectedRowData,
}: ActionBarProps<T>) {
  const { narrowWindow } = useDeviceContext();
  const batchActions = rowActions.filter(
    (action) =>
      action.onBatchClick && !action.hideBatchAction?.(selectedRowData)
  );

  if (!batchActions) return null;

  return (
    <GridLegacy
      item
      xs={12}
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
      }}
    >
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        spacing={actionButtonSpacing}
        sx={{ my: 1 }}
      >
        {_.filter(batchActions, (action) => !action.showInBatchMenu).map(
          (action) => {
            const ActionIcon: TablerIcon = action.getBatchIcon
              ? action.getBatchIcon(selectedRowData)
              : action.getRowIcon
              ? action.getRowIcon(selectedRowData[0])
              : action.Icon;

            const disabledBatchRequirements = _.chain(
              action.getBatchRequirements?.(selectedRowData)
            )
              .filter((r) => r.disabled)
              .value();

            const batchTooltip =
              disabledBatchRequirements.length > 0
                ? `${
                    action.getBatchTooltip?.(selectedRowIds.length) ||
                    action.getLabel(selectedRowData[0])
                  }: ${disabledBatchRequirements
                    .map((r) => r.disabledReason)
                    .join(', ')}`
                : action.getBatchTooltip?.(selectedRowIds.length) ||
                  action.getLabel(selectedRowData[0]);

            return (
              <FullOrIconButton
                key={action.getLabel(selectedRowData[0])}
                onClick={() => {
                  if (action.onBatchClick) {
                    action.onBatchClick(selectedRowData);
                  }
                }}
                tooltip={
                  !selectedRowIds.length
                    ? 'Select at least one row checkbox to enable actions'
                    : batchTooltip
                }
                color={
                  action.color ??
                  action.getColor?.(selectedRowData[0]) ??
                  COLOR_OPTIONS.primary
                }
                disabled={
                  action.disabled ||
                  !selectedRowIds.length ||
                  (!!action.isBatchDisabled &&
                    action.isBatchDisabled(selectedRowData)) ||
                  disabledBatchRequirements.length > 0
                }
                permissionScope={action.permissionScope}
                startIcon={<ActionIcon />}
                label={
                  action.getBatchLabel
                    ? action.getBatchLabel(selectedRowData)
                    : action.getLabel(selectedRowData[0])
                }
                variant="outlined"
                trackingName={action.trackingName}
              />
            );
          }
        )}
        <MoreActionsButton<T>
          rowActions={batchActions}
          selectedRowData={selectedRowData}
          narrowWindow={narrowWindow}
        />
      </Stack>
    </GridLegacy>
  );
}
